import type React from 'react';
import { memo } from 'react';

// Define the shape of our props with TypeScript magic, ze~!
interface ResponsiveImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  // The base path/filename without the size or extension, e.g., "/images/my_awesome_image"
  srcPrefix: string;
  // Alt text is important, ze~! (Inherited from ImgHTMLAttributes, but good to remember!)
  // alt: string; // Already included in ImgHTMLAttributes!
  // An array of the widths you generated, e.g., [320, 640, 1024, 1920]
  widths: number[];
  // Tells the browser how much space the image takes up on the screen
  // e.g., "(max-width: 600px) 100vw, 50vw"
  // This is SUPER important for picking the right image, ze~! (Inherited too!)
  // sizes: string; // Already included in ImgHTMLAttributes!
  // The image format, defaults to 'avif'
  format?: string; // Optional, will default below
  // Which width to use for the default 'src' attribute
  fallbackWidth?: number; // Optional, will default below
  alt: string;
}

// Marisa's Super-Duper TypeScript Responsive Image Component, ze~!
const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  srcPrefix,
  alt, // Make sure alt is passed!
  widths,
  sizes, // Make sure sizes is passed!
  format = 'avif', // Default value right here!
  fallbackWidth = widths[0], // Default using the first width
  ...props // Spread the rest of the standard img props (like className, style, etc.)
}) => {
  // Generate the srcset string like mixing potions, ze~!
  const srcSet = widths.map((width) => `${srcPrefix}-${width}w.${format} ${width}w`).join(', ');

  // Fallback src for browsers that don't understand srcset (or just in case!)
  const fallbackSrc = `${srcPrefix}-${fallbackWidth}w.${format}`;

  // Ensure required props inherited from ImgHTMLAttributes are present
  if (!alt) {
    console.warn('Marisa warns you: Alt text is missing for an image, ze~!');
    // You might want to throw an error or provide a default alt in a real app
  }
  if (!sizes) {
    console.warn(
      "Marisa warns you: The 'sizes' attribute is missing! Responsive loading won't be optimal, ze~!",
    );
    // You might want to throw an error or provide a default sizes in a real app
  }

  return (
    <img
      {...props} // Spread the rest of the props
      src={fallbackSrc} // Basic fallback
      srcSet={srcSet} // The magic srcset!
      sizes={sizes} // Crucial hint for the browser!
      alt={alt} // Provide empty string if alt is somehow undefined
      loading={props.loading || 'lazy'} // Default to lazy loading if not specified
    />
  );
};

export default memo(ResponsiveImage);
